/**
 * authUtils.js - Centralized auth utilities
 * ==========================================
 * Single source of truth for:
 * - Profile completeness check
 * - Auth debugging logs
 * - Error handling
 */

// Debug mode - set to false for production
const AUTH_DEBUG = false;

/**
 * Check if profile is complete
 * 
 * ROBUST CHECK:
 * 1. Primary: onboarding_completed === true
 * 2. Fallback: has role_professional AND (has city OR has company)
 * 
 * This ensures no user gets stuck in an inconsistent state.
 * 
 * @param {Object} profile - The user profile object
 * @returns {boolean}
 */
export const isProfileComplete = (profile) => {
  if (!profile) {
    return false;
  }
  
  // PRIMARY CHECK: onboarding_completed flag
  if (profile.onboarding_completed === true) {
    return true;
  }
  
  // FALLBACK CHECK: has essential fields (for legacy profiles)
  const hasRole = !!profile.role_professional;
  const hasCity = !!(profile.city_id || profile.city);
  const hasCompany = !!profile.company;
  
  // If has role + city, consider complete
  if (hasRole && hasCity) {
    return true;
  }
  
  // If has role + company, consider complete (legacy support)
  if (hasRole && hasCompany) {
    return true;
  }
  
  // Profile incomplete
  return false;
};

/**
 * Get redirect path based on auth state
 * @param {Object} user - Supabase user
 * @param {Object} profile - User profile
 * @param {string} currentPath - Current pathname
 * @returns {string|null} - Redirect path or null if no redirect needed
 */
export const getAuthRedirect = (user, profile, currentPath) => {
  // Not logged in - redirect to login (except for public routes)
  if (!user) {
    const publicRoutes = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/auth/callback'];
    if (publicRoutes.includes(currentPath)) {
      return null;
    }
    return '/login';
  }
  
  const profileComplete = isProfileComplete(profile);
  
  // On onboarding page
  if (currentPath === '/onboarding') {
    if (profileComplete) {
      return '/dashboard';
    }
    // Stay on onboarding
    return null;
  }
  
  // On protected routes with incomplete profile
  if (!profileComplete) {
    return '/onboarding';
  }
  
  // All good, no redirect
  return null;
};

/**
 * Parse Supabase auth error for user-friendly message
 * @param {Error} error - Supabase error
 * @returns {string}
 */
export const parseAuthError = (error) => {
  if (!error) return 'Errore sconosciuto';
  
  const message = (error.message || error.toString()).toLowerCase();
  
  // ========== CREDENZIALI ==========
  if (message.includes('invalid login credentials') || message.includes('invalid_credentials')) {
    return 'Email o password non corretti. Verifica i dati inseriti.';
  }
  
  // ========== UTENTE NON TROVATO ==========
  if (message.includes('user not found') || message.includes('no user found')) {
    return 'Nessun account trovato con questa email. Vuoi registrarti?';
  }
  
  // ========== PASSWORD SBAGLIATA ==========
  if (message.includes('wrong password') || message.includes('incorrect password')) {
    return 'Password non corretta. Riprova o recupera la password.';
  }
  
  // ========== EMAIL NON CONFERMATA ==========
  if (message.includes('email not confirmed')) {
    return 'Email non verificata. Controlla la tua casella di posta e clicca sul link di conferma.';
  }
  
  // ========== UTENTE GIÀ REGISTRATO ==========
  if (message.includes('user already registered') || message.includes('email already in use')) {
    return 'Questa email è già registrata. Prova ad accedere o recupera la password.';
  }
  
  // ========== PASSWORD TROPPO CORTA ==========
  if (message.includes('password should be at least') || message.includes('password too short')) {
    return 'La password deve contenere almeno 6 caratteri.';
  }
  
  // ========== EMAIL NON VALIDA ==========
  if (message.includes('unable to validate email') || message.includes('invalid email')) {
    return 'Indirizzo email non valido. Verifica il formato.';
  }
  
  // ========== TROPPI TENTATIVI ==========
  if (message.includes('rate limit') || message.includes('too many requests')) {
    return 'Troppi tentativi. Attendi qualche minuto prima di riprovare.';
  }
  
  // ========== REGISTRAZIONE DISABILITATA ==========
  if (message.includes('signup disabled') || message.includes('registration disabled')) {
    return 'Le nuove registrazioni sono temporaneamente sospese.';
  }
  
  // ========== ERRORI DI RETE / FETCH ==========
  if (message.includes('network') || message.includes('failed to fetch') || message.includes('fetch failed')) {
    return 'Errore di connessione. Verifica la tua connessione internet e riprova.';
  }
  
  // ========== BODY STREAM ERRORS (tipico di credenziali errate o errore server) ==========
  if (message.includes('body stream') || message.includes('body is disturbed') || message.includes('body is locked') || message.includes('disturbed or locked')) {
    return 'Email o password non corretti. Verifica i dati inseriti.';
  }
  
  // ========== SESSIONE SCADUTA ==========
  if (message.includes('session expired') || message.includes('refresh_token')) {
    return 'Sessione scaduta. Effettua nuovamente l\'accesso.';
  }
  
  // ========== ACCOUNT BLOCCATO ==========
  if (message.includes('banned') || message.includes('blocked') || message.includes('disabled')) {
    return 'Account sospeso. Contatta il supporto per assistenza.';
  }
  
  // ========== ERRORI DATABASE ==========
  if (message.includes('infinite recursion') || message.includes('policy')) {
    return 'Errore di sistema. Il supporto tecnico è stato notificato.';
  }
  
  // ========== TIMEOUT ==========
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Il server non risponde. Riprova tra qualche istante.';
  }
  
  // ========== DEFAULT ==========
  // Se non riconosciuto, mostra messaggio generico ma non l'errore tecnico
  console.error('[Auth] Unhandled error:', message);
  return 'Si è verificato un errore. Riprova o contatta il supporto.';
};

/**
 * Debug log for auth events
 * @param {string} event - Event name
 * @param {Object} data - Event data
 */
export const authLog = (event, data = {}) => {
  // Only log in development or when AUTH_DEBUG is true
  if (!AUTH_DEBUG && process.env.NODE_ENV === 'production') {
    return;
  }
  
  // Only log key events, not every state change
  const keyEvents = [
    'Auth: Initialized',
    'Auth: Session loaded',
    'Auth: Profile loaded', 
    'Auth: Ready',
    'Auth: Signed out',
    'Auth: Error'
  ];
  
  // Always log errors and key events
  const isKeyEvent = keyEvents.some(key => event.includes(key)) || event.includes('Error');
  if (isKeyEvent || AUTH_DEBUG) {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    console.log(`[${timestamp}] 🔐 ${event}`, Object.keys(data).length ? data : '');
  }
};

export default {
  isProfileComplete,
  getAuthRedirect,
  parseAuthError,
  authLog
};
